import React, { useState } from 'react';
import { Popover, Box, Typography, Skeleton, Grow } from '@mui/material';
import { useSelector } from 'react-redux';
import { getFromSession } from '../../app/util';
import { analytics } from '../../hooks/use-analytics';
import { API_BASE_URL } from '../../app/config';
import { VCAP_CHIP_ALLOWED_EMAILS } from '../../app/config-v4-api';
import moment from 'moment';

const CACHE_PREFIX = 'vcap__';

const buildCacheKey = (propertyId, asOfDate, fromDate, toDate) =>
    `${CACHE_PREFIX}${propertyId}__${asOfDate}__${fromDate}_${toDate}`;

const buildDbaCacheKey = (propertyId, stayDate, dbaCount) =>
    `${CACHE_PREFIX}${propertyId}__dba__${stayDate}__range${dbaCount}`;

const readCache = (key) => {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeCache = (key, data) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(data));
    } catch {
        // sessionStorage full or unavailable — skip caching silently
    }
};

const fetchInventory = async (propertyId, asOfDate, fromDate, toDate, isMonthly, token) => {
    const url = `${API_BASE_URL}rms/properties/inventory/${propertyId}/?asOfDate=${asOfDate}&fromDate=${fromDate}&toDate=${toDate}${isMonthly ? '&isMonthly=true' : ''}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
        },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
};

const fetchInventoryByDba = async (propertyId, stayDate, dbaCount, token) => {
    const url = `${API_BASE_URL}rms/properties/inventory/${propertyId}/?stayDate=${stayDate}&dba=${dbaCount}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
        },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
};

// Pickup-diff mode (variance_pickup column): the indicator is comparing two OTB
// snapshots (current vs. prior as-of-date) for the same stay date, so the popup
// shows the DIFFERENCE in OOO/OOS between them rather than a single snapshot's
// counts. The underlying API call is untouched — this just calls fetchInventory
// twice (current asOfDate, prior asOfDate) and diffs the results client-side,
// then reshapes the diff into the same {data: [{stay_date, room_types}]} shape
// the API itself returns, so InventoryContent needs no separate data-parsing path.
const buildDiffInventory = (currentResult, priorResult, stayDate) => {
    const collect = (result) => {
        const map = {};
        (result?.data || []).filter(d => d.stay_date === stayDate).forEach((entry) => {
            (entry.room_types || []).forEach((rt) => {
                if (!map[rt.room_type]) {
                    map[rt.room_type] = { room_type: rt.room_type, display_name: rt.display_name, out_of_order: 0, out_of_service: 0 };
                }
                map[rt.room_type].out_of_order += rt.out_of_order || 0;
                map[rt.room_type].out_of_service += rt.out_of_service || 0;
            });
        });
        return map;
    };
    const currentMap = collect(currentResult);
    const priorMap = collect(priorResult);
    const roomTypeCodes = new Set([...Object.keys(currentMap), ...Object.keys(priorMap)]);
    const room_types = Array.from(roomTypeCodes).map((code) => {
        const cur = currentMap[code];
        const pri = priorMap[code];
        return {
            room_type: code,
            display_name: cur?.display_name || pri?.display_name,
            out_of_order: (cur?.out_of_order || 0) - (pri?.out_of_order || 0),
            out_of_service: (cur?.out_of_service || 0) - (pri?.out_of_service || 0),
        };
    });
    return { data: [{ stay_date: stayDate, room_types }] };
};

const InventoryContent = ({ data, date, fromDate, toDate, isMonthly, vcap, vcapLabel, isPricing, roomType, roomTypes, isDba, stayDate, dba, canSeeVcapChip, isDiff }) => {
    const entries = isDba
        ? (data?.data || []).filter(d => {
            const dVal = d?.dba ?? d?.index?.dba;
            return (dVal === undefined || dVal === null) ? true : dVal === dba;
        })
        : isMonthly
            ? (data?.data || []).filter(d => d.stay_date >= fromDate && d.stay_date <= toDate)
            : (data?.data || []).filter(d => d.stay_date === date);

    const roomTypeTotals = {};
    entries.forEach((entry) => {
        (entry.room_types || []).filter((rt) => {
            if (roomTypes && roomTypes.length) return roomTypes.includes(rt.room_type);
            return !roomType || rt.room_type === roomType;
        }).forEach((rt) => {
            if (!roomTypeTotals[rt.room_type]) {
                roomTypeTotals[rt.room_type] = {
                    label: rt.display_name ? `${rt.display_name} (${rt.room_type})` : rt.room_type,
                    ooo: 0, oos: 0,
                };
            }
            roomTypeTotals[rt.room_type].ooo += rt.out_of_order || 0;
            roomTypeTotals[rt.room_type].oos += rt.out_of_service || 0;
        });
    });
    const rows = [];
    Object.values(roomTypeTotals).forEach(({ label, ooo, oos }) => {
        if (isDiff ? ooo !== 0 : ooo > 0) rows.push({ label, count: ooo, type: 'OOO' });
        if (isDiff ? oos !== 0 : oos > 0) rows.push({ label, count: oos, type: 'OOS' });
    });
    const totalOOO = Object.values(roomTypeTotals).reduce((sum, rt) => sum + rt.ooo, 0);
    const totalOOS = Object.values(roomTypeTotals).reduce((sum, rt) => sum + rt.oos, 0);
    const total = totalOOO + totalOOS;
    const fmt = (n) => (isDiff && n > 0) ? `+${n}` : `${n}`;
    const dateLabel = isDba
        ? `DBA ${dba}${stayDate ? ` (${moment(stayDate).format('MMM DD')})` : ''}`
        : isMonthly
            ? (fromDate ? moment(fromDate).format('MMM YYYY') : '')
            : (date ? moment(date).format('MMM DD') : '');

    // Row styling matches InfoPopover's info-section/data-cell look (table-pricing.scss):
    // SFUI font families, #333 body text, light $Gray4 (#EBEBEB) hairline dividers.
    const rowSx = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 8px',
        borderBottom: '1px solid #EBEBEB',
    };
    const labelSx = { fontFamily: 'SFUI Regular', fontSize: '12px', color: '#333333' };
    const valueSx = { fontFamily: 'SFUI Medium', fontSize: '12px', color: '#333333' };
    const subHeadingSx = { display: 'block', fontFamily: 'SFUI Medium', fontSize: '12px', color: '#333333', padding: '6px 8px 2px' };

    const CountRows = ({ ooo, oos }) => (
        <>
            {(isDiff ? ooo !== 0 : ooo > 0) && (
                <Box sx={rowSx}>
                    <Typography component="span" sx={labelSx}>Out of Order (OOO){isDiff ? ' change' : ''}</Typography>
                    <Typography component="span" sx={valueSx}>{fmt(ooo)}</Typography>
                </Box>
            )}
            {(isDiff ? oos !== 0 : oos > 0) && (
                <Box sx={rowSx}>
                    <Typography component="span" sx={labelSx}>Out of Service (OOS){isDiff ? ' change' : ''}</Typography>
                    <Typography component="span" sx={valueSx}>{fmt(oos)}</Typography>
                </Box>
            )}
        </>
    );

    // Total row gets the same subtle $Gray5 (#F8F9FA) shading InfoPopover uses for its Forecast row.
    const TotalRow = () => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', bgcolor: '#F8F9FA' }}>
            <Typography component="span" sx={{ fontFamily: 'SFUI Medium', fontSize: '12px', color: '#333333' }}>{isDiff ? 'Net change' : 'Total'}</Typography>
            <Typography component="span" sx={{ fontFamily: 'SFUI Medium', fontSize: '12px', color: '#333333' }}>{fmt(total)} rooms</Typography>
        </Box>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header — same grey bar treatment as InfoPopover's .heading */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                bgcolor: '#F5F6FD', padding: '8px', borderTopLeftRadius: '8px', borderTopRightRadius: '8px',
            }}>
                <Typography component="span" sx={{ fontSize: '12px', color: '#333333' }}>
                    {dateLabel} — {isDiff ? 'OOO/OOS change' : 'OOO/OOS'}
                </Typography>
                {vcap && canSeeVcapChip && (
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        bgcolor: '#fff',
                        border: '1px solid #EBEBEB',
                        borderRadius: '4px',
                        px: '6px', py: '2px',
                        flexShrink: 0,
                    }}>
                        <Typography component="span" sx={{ fontSize: '10px', color: '#6B7280' }}>
                            {vcapLabel || 'Var Cap'}:
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '10px', fontFamily: 'SFUI Medium', color: '#333333' }}>
                            {Math.round(vcap).toLocaleString()}
                        </Typography>
                    </Box>
                )}
            </Box>

            {(isDiff ? rows.length === 0 : total === 0) ? (
                <Typography component="span" sx={{ display: 'block', fontFamily: 'SFUI Regular', fontSize: '12px', color: '#9AA0AC', py: '14px', textAlign: 'center' }}>{isDiff ? 'No change in OOO/OOS' : 'No OOO/OOS rooms'}</Typography>
            ) : roomType ? (
                <>
                    <Typography component="span" sx={subHeadingSx}>
                        {Object.values(roomTypeTotals)[0]?.label || roomType}
                    </Typography>
                    <CountRows ooo={totalOOO} oos={totalOOS} />
                    <TotalRow />
                </>
            ) : (roomTypes && roomTypes.length) ? (
                <>
                    {Object.values(roomTypeTotals).filter((rt) => isDiff ? (rt.ooo !== 0 || rt.oos !== 0) : (rt.ooo > 0 || rt.oos > 0)).map((rt, idx) => (
                        <Box key={idx}>
                            <Typography component="span" sx={subHeadingSx}>
                                {rt.label}
                            </Typography>
                            <CountRows ooo={rt.ooo} oos={rt.oos} />
                        </Box>
                    ))}
                    <TotalRow />
                </>
            ) : (
                <>
                    <CountRows ooo={totalOOO} oos={totalOOS} />
                    <TotalRow />
                </>
            )}
        </Box>
    );
};

const VCapIndicator = ({ vcap, vcapLabel, vcapDiffers, variableCapacityEnabled, date, isMonthly, hideRevpar, inline, children, roomType, roomTypes, stayDate, dba, asOfDate: asOfDateProp, diffLookbackDays }) => {
    const { app_state, ui_state } = useSelector((state) => state.app.current_state);
    const { auth } = useSelector((state) => state);
    const canSeeVcapChip = VCAP_CHIP_ALLOWED_EMAILS.includes(auth?.user?.email);
    const isPricing = !!hideRevpar || !!(ui_state?.active_tiles?.includes('pricing'));
    const propertyId = app_state?.property_id;
    // Callers on columns whose header carries its own as-of date (e.g. the Pickup tile's
    // "OTB As of - <as_of - pickUpDays>" column) pass it in; everything else uses the app's as-of.
    const asOfDate = moment(asOfDateProp || app_state?.as_of_date).format('YYYY-MM-DD');
    const rowDate = date ? moment(date).format('YYYY-MM-DD') : null;
    const fromDate = rowDate;
    const toDate = rowDate ? (isMonthly ? moment(rowDate).endOf('month').format('YYYY-MM-DD') : rowDate) : null;
    const isDba = (dba !== undefined && dba !== null) && !!stayDate;
    const stayDateFormatted = stayDate ? moment(stayDate).format('YYYY-MM-DD') : null;
    const dbaCount = app_state?.workspace_controls?.dba;
    // Pickup-diff mode: diffLookbackDays is the same pickUpDays lookback the report
    // itself uses to compute "prior" — the row's Occ/Available already diff current
    // vs. prior; this indicator's popup should diff the same two snapshots' OOO/OOS.
    const isDiffMode = !isDba && !!diffLookbackDays;
    const priorAsOfDate = isDiffMode ? moment(app_state?.as_of_date).subtract(diffLookbackDays, 'days').format('YYYY-MM-DD') : null;

    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    if (!variableCapacityEnabled) return inline ? (children || null) : null;
    if (!vcap) return inline ? (children || null) : null;
    if (!vcapDiffers) return inline ? (children || null) : null;

    const handleClick = async (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);

        // Same routing as the capacity toggle / view switch — lands on the active tile's
        // own event, with the OOO/OOS details view riding along as a property.
        analytics.track("tile_change", {
            "selected_tile": ui_state?.active_tiles?.join(),
            vcap_details: isDba ? 'dba' : isMonthly ? 'monthly' : 'daily',
            stay_date: isDba ? stayDateFormatted : rowDate,
            as_of_date: asOfDate,
        });

        if (isDba) {
            if (!stayDateFormatted || !propertyId || dbaCount === undefined || dbaCount === null) return;
        } else if (!fromDate || !toDate || !propertyId || !asOfDate || (isDiffMode && !priorAsOfDate)) {
            return;
        }

        const cacheKey = isDba
            ? buildDbaCacheKey(propertyId, stayDateFormatted, dbaCount)
            : isDiffMode
                ? `${buildCacheKey(propertyId, asOfDate, fromDate, toDate)}__vs__${priorAsOfDate}`
                : buildCacheKey(propertyId, asOfDate, fromDate, toDate);
        const cached = readCache(cacheKey);
        if (cached) {
            setData(cached);
            return;
        }

        setLoading(true);
        setError(null);
        setData(null);

        try {
            const token = getFromSession('token');
            let result;
            if (isDba) {
                result = await fetchInventoryByDba(propertyId, stayDateFormatted, dbaCount, token);
            } else if (isDiffMode) {
                // Same fetchInventory call as the non-diff path, just called twice —
                // the API itself is unchanged.
                const [currentResult, priorResult] = await Promise.all([
                    fetchInventory(propertyId, asOfDate, fromDate, toDate, isMonthly, token),
                    fetchInventory(propertyId, priorAsOfDate, fromDate, toDate, isMonthly, token),
                ]);
                result = buildDiffInventory(currentResult, priorResult, fromDate);
            } else {
                result = await fetchInventory(propertyId, asOfDate, fromDate, toDate, isMonthly, token);
            }
            writeCache(cacheKey, result);
            setData(result);
        } catch (err) {
            setError('Failed to load inventory data.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Box
                component="span"
                data-testid="vcap-indicator"
                onClick={handleClick}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '4px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '18px',
                        height: '2px',
                        backgroundColor: '#FFA726',
                        borderRadius: '1px',
                        display: 'block',
                    },
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                TransitionComponent={Grow}
                transitionDuration={{ enter: 380, exit: 300 }}
                TransitionProps={{
                    easing: {
                        enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                        exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                }}
                onClick={(e) => e.stopPropagation()}
                className="info-popover"
                PaperProps={{
                    sx: {
                        ml: '12px',
                        width: 300,
                        p: 0,
                        border: 'solid 1px #efefef',
                        borderRadius: '8px',
                        boxShadow: '0px 0px 6px #ccc',
                        overflow: 'visible',
                        position: 'relative',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            left: '-11px',
                            top: '50%',
                            width: 0,
                            height: 0,
                            borderTop: '9px solid transparent',
                            borderBottom: '9px solid transparent',
                            borderRight: '11px solid #F8F9FA',
                            transform: 'translateY(-50%)',
                            filter: 'drop-shadow(-5px -4px 16px rgba(0, 0, 0, 0.9))',
                            zIndex: 1,
                        },
                    },
                }}
            >
                {loading && (
                    <Box sx={{ width: '100%' }}>
                        {/* Header skeleton — matches .heading grey bar */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                            bgcolor: '#F5F6FD', padding: '8px', borderTopLeftRadius: '8px', borderTopRightRadius: '8px',
                        }}>
                            <Skeleton variant="text" width="55%" height={18} sx={{ borderRadius: '4px' }} />
                            <Skeleton variant="rounded" width={56} height={16} sx={{ borderRadius: '4px', flexShrink: 0 }} />
                        </Box>

                        {/* OOO / OOS row skeletons */}
                        {[1, 2].map((i) => (
                            <Box key={i} sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '6px 8px', borderBottom: '1px solid #EBEBEB',
                            }}>
                                <Skeleton variant="text" width="45%" height={16} sx={{ borderRadius: '4px' }} />
                                <Skeleton variant="text" width="15%" height={16} sx={{ borderRadius: '4px' }} />
                            </Box>
                        ))}

                        {/* Total row skeleton */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', bgcolor: '#F8F9FA' }}>
                            <Skeleton variant="text" width="24%" height={16} sx={{ borderRadius: '4px' }} />
                            <Skeleton variant="text" width="20%" height={16} sx={{ borderRadius: '4px' }} />
                        </Box>
                    </Box>
                )}

                {error && (
                    <Typography component="span" sx={{ display: 'block', fontFamily: 'SFUI Regular', fontSize: '12px', color: '#d32f2f', padding: '8px' }}>{error}</Typography>
                )}

                {data && <InventoryContent data={data} date={rowDate} fromDate={fromDate} toDate={toDate} isMonthly={isMonthly} vcap={vcap} vcapLabel={vcapLabel} isPricing={isPricing} roomType={roomType} roomTypes={roomTypes} isDba={isDba} stayDate={stayDateFormatted} dba={dba} canSeeVcapChip={canSeeVcapChip} isDiff={isDiffMode} />}
            </Popover>
        </>
    );
};

export default VCapIndicator;
